import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Container, Box, Modal,
  Stack, TextField, MenuItem, IconButton, Alert,
  Drawer, List, ListItemButton, ListItemText, Divider, Menu, ListSubheader,
  Link as MuiLink,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../utils/serviceName';
import { isSalonHost } from '../utils/host';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const {
    user, loading: loadingUser, workspaces, activeWorkspaceId,
    activeWorkspace, workspaceRole, switchWorkspace, logout: doLogout,
  } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Публічні сторінки салону (/w/:slug) АБО весь застосунок на піддомені салону
  // (<slug>.домен) мають власну брендовану шапку — платформний AppBar ховаємо.
  const brandedPublic = location.pathname.startsWith('/w/') || isSalonHost();

  // --- стан для модалки ---
  const [billingBanner, setBillingBanner] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copySuccess, setCopySuccess] = useState('');

  // --- Відкрити модалку і підвантажити послуги ---
  const handleOpenInvite = async () => {
    setInviteOpen(true);
    setInviteLink('');
    setCopySuccess('');
    setSelectedServiceId('');
    try {
      if (user && user.id) {
        const services = await api.getServices(user.id);
        setAllServices(services);
      }
    } catch {
      setAllServices([]);
    }
  };

  const handleGenerateLink = async () => {
    setInviteLink('');
    setCopySuccess('');
    try {
      const res = await api.createBookingLink({
        masterId: user.id,
        serviceId: selectedServiceId || undefined,
      });
      setInviteLink(res.url);
    } catch (e) {
      setInviteLink(t('invite.error'));
    }
  };

  const handleCopy = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopySuccess(t('common.copied'));
    setTimeout(() => setCopySuccess(''), 1500);
  };

  const handleLogout = async () => {
    await doLogout();
    navigate('/login');
  };

  // Банер білінгу (тріал спливає / несплата / призупинено) — власнику/адміну салону.
  useEffect(() => {
    let ignore = false;
    if (!activeWorkspaceId || !(workspaceRole === 'owner' || workspaceRole === 'admin')) {
      setBillingBanner(null);
      return;
    }
    api.getWorkspaceBilling(activeWorkspaceId)
      .then((r) => {
        if (ignore) return;
        const b = r.billing;
        const show = b && (!b.ok || ['past_due', 'trial_ending'].includes(b.banner));
        setBillingBanner(show ? b : null);
      })
      .catch(() => { if (!ignore) setBillingBanner(null); });
    return () => { ignore = true; };
  }, [activeWorkspaceId, workspaceRole]);

  // --- Пункти навігації за роллю ---
  const navItems = [];
  // Каталог салонів (маркетплейс) тимчасово прихований — увімкнемо, коли буде
  // достатньо салонів. Роут /discover лишається доступним за прямим URL.
  // navItems.push({ label: t('nav.discover'), to: '/discover' });
  if (user?.role === 'user') {
    // Клієнт бронює через сторінку конкретного салону — загального «/book» немає
    navItems.push({ label: t('nav.cabinet'), to: '/user/cabinet' });
  }
  if (user?.role === 'master' || user?.role === 'admin') {
    navItems.push({ label: t('nav.generateLink'), onClick: handleOpenInvite });
    navItems.push({ label: t('nav.panel'), to: '/master/dashboard', resetWeek: true });
    navItems.push({ label: t('nav.schedule'), to: '/master/schedule' });
    navItems.push({ label: t('nav.profile'), to: '/master/services' });
    navItems.push({ label: t('nav.accounting'), to: '/master/accounting' });
    navItems.push({ label: t('nav.promos'), to: '/master/promos' });
  }
  // God, що «увійшов» у салон, — доступ до панелі салону (як власник)
  if (user?.role === 'superadmin' && workspaceRole) {
    navItems.push({ label: t('nav.panel'), to: '/master/dashboard', resetWeek: true });
  }
  // Клієнти салону — будь-який учасник активного воркспейсу
  if (workspaceRole) {
    navItems.push({ label: t('nav.clients'), to: '/workspace/clients' });
  }
  // Керування командою — власник/адмін активного воркспейсу
  if (workspaceRole === 'owner' || workspaceRole === 'admin') {
    navItems.push({ label: t('nav.team'), to: '/workspace/team' });
  }
  // Профіль/дизайн/лендінг/правила — лише власник; згруповані у випадайку «Налаштування»,
  // щоб не переповнювати шапку.
  const salonSettings = [];
  if (workspaceRole === 'owner') {
    salonSettings.push({ label: t('nav.salonProfile'), to: '/workspace/profile' });
    salonSettings.push({ label: t('nav.design'), to: '/workspace/theme' });
    salonSettings.push({ label: t('nav.terms'), to: '/workspace/terms' });
    salonSettings.push({ label: t('nav.billing'), to: '/workspace/billing' });
  }
  // Платформна панель — лише superadmin (God)
  if (user?.role === 'superadmin') {
    navItems.push({ label: t('nav.platform'), to: '/admin/workspaces' });
    navItems.push({ label: t('nav.adminBilling'), to: '/admin/billing' });
  }
  const tailItems = []; // праворуч після налаштувань
  if (user) {
    tailItems.push({ label: t('nav.logout'), onClick: handleLogout });
  } else {
    navItems.push({ label: t('nav.login'), to: '/login' });
    navItems.push({ label: t('nav.register'), to: '/register' });
  }

  const handleNav = (item) => {
    setDrawerOpen(false);
    if (item.onClick) item.onClick();
    else if (item.to) {
      if (item.resetWeek) {
        // Явний перехід у «Панель» — завжди поточний тиждень
        sessionStorage.setItem('dashWeekOffset', '0');
        navigate(item.to, { state: { resetWeek: true } });
      } else {
        navigate(item.to);
      }
    }
  };

  // Брендована публічна сторінка салону — рендеримо контент без платформного
  // хрому (сторінка сама малює свою шапку/тему на всю ширину).
  if (brandedPublic) {
    return <Box>{children}</Box>;
  }

  // Гість на корені платформи «/» бачить промо-лендінг з власною навігацією —
  // платформний AppBar/футер ховаємо (лендінг малює все сам, на всю ширину).
  if (!user && location.pathname === '/' && !isSalonHost()) {
    return <Box>{children}</Box>;
  }

  // --- Поки користувач не завантажився, показувати лоадер/плейсхолдер ---
  if (loadingUser) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" color="text.secondary">{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <AppBar position="static">
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, flexShrink: 0, mr: 1 }} noWrap>
            <Link to="/" style={{ color: '#fff', textDecoration: 'none' }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{
                  width: 30, height: 30, borderRadius: '9px', flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(135deg, #6c1129 0%, #47091b 100%)',
                  color: '#fff', fontWeight: 800, fontSize: 17, lineHeight: 1,
                  boxShadow: '0 2px 8px rgba(108,17,41,0.45)',
                }}>a</Box>
                <Box component="span" sx={{ fontWeight: 800, letterSpacing: '-0.01em', display: { xs: 'none', sm: 'inline' } }}>
                  appoint<Box component="span" sx={{ color: '#c9788c' }}>Me</Box>
                </Box>
              </Box>
            </Link>
          </Typography>

          {/* Десктоп (lg+): інлайн-навігація */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 0.5, alignItems: 'center', minWidth: 0 }}>
            {workspaces.length > 0 && (
              <TextField
                select
                size="small"
                variant="standard"
                value={activeWorkspaceId || ''}
                onChange={(e) => switchWorkspace(Number(e.target.value))}
                aria-label={t('workspace.switcher')}
                sx={{
                  minWidth: 120, maxWidth: 180, mr: 0.5,
                  '& .MuiInput-root': { color: '#fff' },
                  '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255,255,255,0.4)' },
                  '& .MuiSvgIcon-root': { color: '#fff' },
                }}
              >
                {workspaces.map((w) => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
              </TextField>
            )}
            {navItems.map((item) => (
              <Button key={item.label} color="inherit" size="small" onClick={() => handleNav(item)}
                sx={{ whiteSpace: 'nowrap', px: 1 }}>
                {item.label}
              </Button>
            ))}
            {salonSettings.length > 0 && (
              <>
                <Button color="inherit" size="small" endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => setSettingsAnchor(e.currentTarget)} sx={{ whiteSpace: 'nowrap', px: 1 }}>
                  {t('nav.salonSettings')}
                </Button>
                <Menu anchorEl={settingsAnchor} open={!!settingsAnchor} onClose={() => setSettingsAnchor(null)}>
                  {salonSettings.map((item) => (
                    <MenuItem key={item.label} onClick={() => { setSettingsAnchor(null); navigate(item.to); }}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            {tailItems.map((item) => (
              <Button key={item.label} color="inherit" size="small" onClick={() => handleNav(item)}
                sx={{ whiteSpace: 'nowrap', px: 1 }}>
                {item.label}
              </Button>
            ))}
            <LanguageSwitcher />
          </Box>

          {/* Планшет/мобільний (<lg): перемикач мов + гамбургер */}
          <Box sx={{ display: { xs: 'inline-flex', lg: 'none' }, alignItems: 'center' }}>
            <LanguageSwitcher />
            <IconButton
              color="inherit"
              edge="end"
              aria-label={t('nav.menu')}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Мобільне меню */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260 }} role="presentation">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
            <Typography variant="h6">{t('nav.menu')}</Typography>
            <IconButton size="small" onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Stack>
          <Divider />
          {workspaces.length > 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <TextField
                select
                size="small"
                fullWidth
                label={t('workspace.switcher')}
                value={activeWorkspaceId || ''}
                onChange={(e) => switchWorkspace(Number(e.target.value))}
              >
                {workspaces.map((w) => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
              </TextField>
            </Box>
          )}
          <List>
            {navItems.map((item) => (
              <ListItemButton key={item.label} onClick={() => handleNav(item)}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
            {salonSettings.length > 0 && (
              <>
                <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
                  {t('nav.salonSettings')}
                </ListSubheader>
                {salonSettings.map((item) => (
                  <ListItemButton key={item.label} onClick={() => handleNav(item)} sx={{ pl: 3 }}>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
                <Divider />
              </>
            )}
            {tailItems.map((item) => (
              <ListItemButton key={item.label} onClick={() => handleNav(item)}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
      {/* --- Модалка для запрошення клієнта --- */}
      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper', color: 'text.primary',
          border: '1px solid', borderColor: 'divider',
          borderRadius: 2, p: { xs: 3, sm: 4 },
          width: '90%', maxWidth: 400
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('invite.title')}</Typography>
            <IconButton onClick={() => setInviteOpen(false)} size="small" sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Typography sx={{ mt: 2, mb: 1 }}>{t('invite.pickService')}</Typography>
          <TextField
            select
            label={t('invite.service')}
            value={selectedServiceId}
            onChange={e => setSelectedServiceId(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="">{t('invite.anyService')}</MenuItem>
            {allServices.map(s =>
              <MenuItem key={s.id} value={s.id}>{pickServiceName(s, i18n.language)}</MenuItem>
            )}
          </TextField>
          <Button
            variant="contained"
            fullWidth
            onClick={handleGenerateLink}
            sx={{ fontWeight: 600, borderRadius: 2, textTransform: 'none', mb: 2 }}
          >
            {t('invite.generate')}
          </Button>
          {inviteLink && (
            <Stack spacing={1} alignItems="center">
              <TextField
                value={inviteLink}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={handleCopy} size="small">
                      <ContentCopyIcon color="primary" />
                    </IconButton>
                  )
                }}
                fullWidth
                sx={{ mb: 1 }}
              />
              {copySuccess && <Alert severity="success">{copySuccess}</Alert>}
            </Stack>
          )}
        </Box>
      </Modal>
      <Container sx={{ mt: 4 }}>
        {activeWorkspace?.status === 'pending' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('workspace.pendingBanner')}
          </Alert>
        )}
        {billingBanner && (
          <Alert
            severity={!billingBanner.ok ? 'error' : billingBanner.banner === 'past_due' ? 'warning' : 'info'}
            sx={{ mb: 2 }}
            action={
              workspaceRole === 'owner' ? (
                <Button color="inherit" size="small" onClick={() => navigate('/workspace/billing')}>
                  {t('billing.manageShort')}
                </Button>
              ) : null
            }
          >
            {!billingBanner.ok
              ? t('billing.bannerSuspended')
              : billingBanner.banner === 'past_due'
              ? t('billing.bannerPastDue')
              : t('billing.bannerTrialEnding')}
          </Alert>
        )}
        {children}
      </Container>

      {/* Футер із юридичними посиланнями */}
      <Box component="footer" sx={{ mt: 6, py: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <Container>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} appointMe
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <MuiLink component={Link} to="/terms" variant="caption" color="text.secondary" underline="hover">{t('nav.terms')}</MuiLink>
              <MuiLink component={Link} to="/privacy" variant="caption" color="text.secondary" underline="hover">{t('legal.privacy')}</MuiLink>
              <MuiLink component={Link} to="/cookies" variant="caption" color="text.secondary" underline="hover">{t('legal.cookies')}</MuiLink>
              <MuiLink component={Link} to="/dpa" variant="caption" color="text.secondary" underline="hover">{t('legal.dpa')}</MuiLink>
              <MuiLink component={Link} to="/refund" variant="caption" color="text.secondary" underline="hover">{t('legal.refund')}</MuiLink>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
